import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { map, Observable, of, startWith } from 'rxjs';
import { ApiValidation, ConfirmDialogComponent, Logger } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { Event, EventService, Store, Version, VersionService, VersionStoreService } from '../..';
import { VersionListDialogComponent } from '../version-list-dialog/version-list-dialog.component';

const log = new Logger('VersionDetailsComponent');

@Component({
  selector: 'app-version-details',
  templateUrl: './version-details.component.html',
  styleUrls: ['./version-details.component.scss']
})

export class VersionDetailsComponent<TEvent extends Event, TVersion extends Version> implements OnInit {
  @Input() versionId: string = '0';
  @Input() fromVersionId: string = '0';
  @Input() eventId = '';
  @Input() storesToVersion: Store[] = [];
  @Output() versionDetails = new EventEmitter<TVersion>();
  @Output() closeDialog = new EventEmitter<string>();

  formGroup = new FormGroup({
    ClientKey: new FormControl<string>(''),
    Id: new FormControl<string>('0', { nonNullable: true }),
    VersionName: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
    Disclaimer: new FormControl<string>(''),
    VersionIdForOffers: new FormControl<string | null>(null),
  });

  version$: Observable<TVersion> = of({} as TVersion);
  distinctVersionNames: Array<string> = [];
  filteredVersionNames$: Observable<string[]> | undefined;
  existingVersions: TVersion[] = [];
  eventClientKey? = '';
  selectedVersionName: string = '';
  viewModel$!: Observable<{ event: TEvent }>;

  constructor(
    private versionStoreService: VersionStoreService<Store>,
    private versionService: VersionService<TVersion>,
    private eventService: EventService<TEvent>,
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    log.debug('init');

    if (this.eventId) {
      this.viewModel$ = this.eventService.getEvent(this.eventId).pipe(
        map(event => {
          this.eventClientKey = event.ClientKey;
          return { event };
        })
      );

      this.versionService.getVersionsForVariants(this.eventId).subscribe((versions) => {
        this.existingVersions = versions;
        this.setSelectedVersionName();
      });
    }

    // if we are editing a record, get the record data
    if (this.versionId !== '0') {
      this.versionService.getVersion(this.versionId).subscribe((record) => {
        this.formGroup.patchValue(record);
        this.formGroup.markAllAsTouched();
        this.versionDetails.emit(record);
        this.setSelectedVersionName();
      });
    }

    // get the distinct list of version names to populate the autocomplete
    this.versionService.getDistinctVersionNames().subscribe((names) => {
      this.distinctVersionNames = names.value;
    });

    this.filteredVersionNames$ = this.formGroup.controls.VersionName.valueChanges.pipe(
      startWith(''),
      map(value => this.filterVersionNames(value ? value : '')),
    );

    this.formGroup.get('VersionName')?.valueChanges.subscribe(() => {
      ['VersionName', 'ClientKey', 'Id'].forEach(controlName => {
        const control = this.formGroup.get(controlName);
        if (control?.hasError('invalid')) {
          const errors = control.errors;
          if (errors) {
            delete errors['invalid'];
            if (Object.keys(errors).length === 0) {
              control.setErrors(null);
            } else {
              control.setErrors(errors);
            }
            control.updateValueAndValidity();
          }
        }
      });

      this.formGroup.updateValueAndValidity();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['versionId']) {
      this.versionId = changes['versionId'].currentValue;
      if (this.versionId !== '0') {
        this.versionService.getVersion(this.versionId).subscribe((record) => {
          this.formGroup.patchValue(record);
          this.formGroup.markAllAsTouched();
          this.setSelectedVersionName();
        });
      }
      else {
        this.formGroup.reset();
        this.formGroup.patchValue({ Id: '0', ClientKey: '' });
        this.selectedVersionName = '';
      }
    }
  }

  filterVersionNames(value: string): string[] {
    const filterValue = value?.toLowerCase();
    return this.distinctVersionNames.filter(option => option.toLowerCase().includes(filterValue));
  }

  trimControlValues(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup) {
        this.trimControlValues(abstractControl);
      } else {
        if (typeof abstractControl?.value == 'string') {
          abstractControl.setValue(abstractControl?.value.trim());
        }
      }
    })
  }

  save(): void {
    this.trimControlValues(this.formGroup);

    if (this.formGroup.valid) {
      const rawValue = this.formGroup.getRawValue();
      const versionPayload = { ...rawValue } as any;
      delete versionPayload.VersionIdForOffers; 

      const clientKeyValue = this.eventClientKey + '-' + (versionPayload as TVersion).VersionName;
      this.formGroup.patchValue({ ClientKey: clientKeyValue });
      versionPayload.ClientKey = clientKeyValue;

      this.versionService.saveVersion(this.eventId, this.formGroup.controls.VersionIdForOffers.value, versionPayload as TVersion).subscribe({
        next: (response) => {
          if (this.storesToVersion.length > 0 && this.versionId === '0' && this.fromVersionId) {
            this.versionStoreService.moveToVersion(this.fromVersionId, response.Id, this.storesToVersion).subscribe(
              {
                next: () => {
                  this.matSnackBar.open(
                    `${response.VersionName} created, stores moved successfully`,
                    'OK',
                    DEFAULT_SNACKBAR_CONFIG
                  );

                  this.versionService.getVersion(response.Id).subscribe((record) => {
                    this.versionDetails.emit(record);
                    this.versionService.reload();
                  });
                },
                error: (error) => {
                  log.error('Error moving stores to new version', error);
                  this.matSnackBar.open(
                    `Failed to move stores to ${response.VersionName}`,
                    'Dismiss',
                    DEFAULT_SNACKBAR_CONFIG
                  );
                }
              }
            )
          } else {
            this.matSnackBar.open(
              `${this.formGroup.controls.VersionName.value} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG
            );
            this.versionService.getVersion(response.Id).subscribe((record) => {
              this.versionDetails.emit(record);
              this.versionService.reload();
            });
          }
        },
        error: (error) => {
          if (error.status === 500) {
            log.error('500 Error saving version ', error);
            this.matSnackBar.open('500 Error saving version', 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
          }
          if (error.status === 400) {
            const apiValidations: ApiValidation[] = error.error;

            apiValidations.forEach((validation) => {
              if (this.formGroup.get(validation.PropertyName)) {
                const control = this.formGroup.get(validation.PropertyName);
                if (control) {
                  control.markAsTouched();
                  control.setErrors({ invalid: validation.ErrorMessage });
                }
              } else {
                ///TODO: if we have cross field validation then show the validation error at the top of the screen
                // if we have cross field validation then show the validation error at the top of the screen
                // push general error messages to array this is displayed in a toast or dialog
              }
            });
          }
        }
      });
    }
  }

  cancel(): void {
    this.formGroup.reset();
    this.closeDialog.emit('cancelled');
  }

  deleteRecord() {
    const record = this.formGroup.getRawValue() as TVersion;
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you wish to delete ${record.VersionName}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(
      confirmResult => {
        if (confirmResult) {
          this.versionService.deleteVersion(record.Id).subscribe({
            next: () => {
              this.matSnackBar.open(`${record.VersionName} deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
              this.versionService.reload();
              this.closeDialog.emit('deleted');
            },
            error: (error) => {
              log.error('Error deleting event', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            }
          });
        }
      });
  }

  ngOnDestroy() { }

  private setSelectedVersionName() {
    if (this.formGroup.controls.VersionIdForOffers.value && this.existingVersions.length > 0) {
      const paired = this.existingVersions.find(v => v.Id === this.formGroup.controls.VersionIdForOffers.value);
      this.selectedVersionName = paired ? paired.VersionName : '';
    } else {
      this.selectedVersionName = '';
    }
  }

  openVersionListDialog() {
    const dialogRef = this.dialog.open(VersionListDialogComponent, {
      width: '40%',
      height: '80%',
      data: {event: this.eventId, mode: 'PAIR_WITH_EXISTING', excludedVersionIds: this.versionId !== '0' ? [this.versionId] : []
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.formGroup.patchValue({ VersionIdForOffers: result.Id });
        this.selectedVersionName = result.VersionName;
      }
    });
  }

  clearVersionSelection(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedVersionName = '';
    this.formGroup.patchValue({ VersionIdForOffers: null });
  }
}
